/**
 * VotingBar Component - Step 7: Voting System
 * Allows users to vote: Flop (👎), Issa Hit (🔥), Rocking With It (🎸)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThumbsDown, Flame, Guitar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VotingBar = ({ trackId, initialVoteCounts, initialUserVote, compact = false }) => {
  const { isAuthenticated } = useAuth();
  const [userVote, setUserVote] = useState(initialUserVote || null);
  const [voteCounts, setVoteCounts] = useState(initialVoteCounts || {
    flop: 0,
    issa_hit: 0,
    rocking: 0
  });
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // Fetch user's vote on mount
  useEffect(() => {
    if (isAuthenticated && trackId) {
      fetchUserVote();
    }
  }, [isAuthenticated, trackId]);

  const fetchUserVote = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(
        `${API_URL}/api/artists/radio/tracks/${trackId}/vote`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data) {
        setUserVote(response.data.user_vote);
        setVoteCounts(response.data.vote_counts);
      }
    } catch (err) {
      console.error('Error fetching vote:', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleVote = async (voteType) => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      setTimeout(() => setShowLoginPrompt(false), 3000);
      return;
    }

    if (loading || syncing) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/api/artists/radio/tracks/${trackId}/vote`,
        { vote_type: voteType },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setUserVote(response.data.vote_type);
        setVoteCounts(response.data.vote_counts);
      }
    } catch (err) {
      console.error('Error voting:', err);
    } finally {
      setLoading(false);
    }
  };

  const voteButtons = [
    {
      type: 'flop',
      icon: ThumbsDown,
      label: 'Flop',
      color: 'from-red-500 to-red-600',
      bgActive: 'bg-red-500/20',
      textActive: 'text-red-400'
    },
    {
      type: 'issa_hit',
      icon: Flame,
      label: 'Issa Hit',
      color: 'from-[#FF007F] to-orange-500',
      bgActive: 'bg-[#FF007F]/20',
      textActive: 'text-[#FF007F]'
    },
    {
      type: 'rocking',
      icon: Guitar,
      label: 'Rocking',
      color: 'from-[#00F0FF] to-purple-500',
      bgActive: 'bg-[#00F0FF]/20',
      textActive: 'text-[#00F0FF]'
    }
  ];

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {voteButtons.map(({ type, icon: Icon, bgActive, textActive }) => (
          <motion.button
            key={type}
            whileTap={{ scale: 0.9 }}
            onClick={() => handleVote(type)}
            disabled={loading || syncing}
            className={`
              p-2 rounded-full transition-all
              ${userVote === type 
                ? `${bgActive} ${textActive}` 
                : 'bg-white/5 text-[#71717A] hover:bg-white/10 hover:text-white'
              }
            `}
            data-testid={`vote-${type}-btn`}
          >
            <Icon className="w-4 h-4" />
          </motion.button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center justify-center gap-3">
        {voteButtons.map(({ type, icon: Icon, label, color, bgActive, textActive }) => (
          <motion.button
            key={type}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleVote(type)}
            disabled={loading || syncing}
            className={`
              flex flex-col items-center gap-1 p-3 rounded-xl transition-all min-w-[80px]
              ${userVote === type 
                ? `${bgActive} border border-current ${textActive}` 
                : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 hover:text-white border border-transparent'
              }
              ${loading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            data-testid={`vote-${type}-btn`}
          >
            <div className={`
              w-10 h-10 rounded-full flex items-center justify-center
              ${userVote === type ? `bg-gradient-to-br ${color}` : 'bg-white/10'}
            `}>
              <Icon className={`w-5 h-5 ${userVote === type ? 'text-white' : ''}`} />
            </div>
            <span className="text-xs font-medium">{label}</span>
            <span className="text-xs opacity-60">{voteCounts[type] || 0}</span>
          </motion.button>
        ))}
      </div>

      {/* Net Score Display */}
      <div className="mt-4 text-center">
        {(() => {
          const netScore = 2 * (voteCounts.issa_hit || 0) + (voteCounts.rocking || 0) - (voteCounts.flop || 0);
          return (
            <p className="text-[#71717A] text-xs">
              Net Score:{' '}
              <span className={`font-semibold ${netScore > 0 ? 'text-green-400' : netScore < 0 ? 'text-red-400' : 'text-white'}`}>
                {netScore > 0 ? '+' : ''}{netScore}
              </span>
            </p>
          );
        })()}
      </div>

      {/* Login Prompt */}
      <AnimatePresence>
        {showLoginPrompt && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute -top-16 left-1/2 -translate-x-1/2 px-4 py-2 rounded-xl bg-[#12101A] border border-white/10 text-white text-sm whitespace-nowrap shadow-xl"
          >
            Sign in to vote on tracks
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VotingBar;
